import { Flex, MantineSize, Select, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ResourceAvatar } from '@medplum/react';
import { useMemo } from 'react';

interface PractitionerInputProps {
  value: Practitioner | null;
  onChange: (resource: Practitioner | null) => void;
  disabled?: boolean;
  practitioners?: (Practitioner & { roleLabel?: string })[];
  label?: string;
  required?: boolean;
  clearable?: boolean;
  /** Mantine input size. Defaults to Mantine's own default so existing callers are unchanged. */
  size?: MantineSize;
  /** Lets the input share a flex row with the controls beside it. */
  flex?: number;
}

interface PractitionerSelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  practitioner: Practitioner;
  roleLabel?: string;
}

function PractitionerSelectItem({ label, practitioner, roleLabel, ...others }: PractitionerSelectItemProps) {
  return (
    <Flex gap="xs" align="center" direction="row" {...others}>
      <ResourceAvatar value={practitioner} radius="xl" />
      <Flex direction="column">
        <Text>{label}</Text>
        {roleLabel && (
          <Text size="xs" c="dimmed">
            {roleLabel}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export function PractitionerInput({
  value,
  onChange,
  disabled,
  practitioners,
  label = 'Provider',
  required = true,
  clearable = false,
  size,
  flex,
}: PractitionerInputProps) {
  const data = useMemo(
    () =>
      (practitioners ?? [])
        .map((p) => ({
          value: p.id ?? '',
          label: formatHumanName(p.name?.[0]) ?? '',
          practitioner: p,
        }))
        .filter((option) => option.value && option.label),
    [practitioners]
  );

  const handleChange = (id: string | null) => {
    if (!id) {
      onChange(null);
      return;
    }

    const practitioner = practitioners?.find((p) => p.id === id) ?? null;
    onChange(practitioner);
  };

  return (
    <Select
      size={size}
      flex={flex}
      miw={0}
      label={label}
      placeholder="Select provider"
      data={data}
      value={value?.id ?? null}
      onChange={handleChange}
      disabled={disabled}
      searchable
      clearable={clearable}
      renderOption={(input) => {
        const option = input.option as (typeof data)[number];
        return (
          <PractitionerSelectItem
            label={option.label}
            practitioner={option.practitioner}
            roleLabel={option.practitioner.roleLabel}
          />
        );
      }}
      nothingFoundMessage="No providers"
      required={required}
    />
  );
}
